import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DoNotDisturbOnOutlinedIcon from "@mui/icons-material/DoNotDisturbOnOutlined";
import { simpleFetch } from "src/utils/helpsers";
import { serbIrns, notLoadedDistricts, nonUnionDistricts, snapshotDate } from "src/data/serbSnapshot";

// Normalize IRN to 6-digit zero-padded string
const padIrn = (irn) => String(irn).padStart(6, "0");

function SerbContractStatus() {
  const [loadedIrns, setLoadedIrns] = useState(null);
  const [error, setError] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);

  useEffect(() => {
    const endpoint = `${process.env.REACT_APP_API_URL}/rpc/serb_contract_irns?_year=2026`;
    simpleFetch(endpoint)
      .then((data) => {
        // PostgREST 8.x returns [[irn1, irn2, ...]]
        const irns = Array.isArray(data)
          ? data.map((row) => padIrn(typeof row === "object" ? row.irn : row))
          : [];
        setLoadedIrns(new Set(irns));
      })
      .catch((err) => {
        console.error("Failed to fetch SERB contract IRNs:", err);
        setError("Could not load contract status data.");
      });
  }, []);

  if (error) {
    return (
      <Box sx={{ mt: 2, p: 2, bgcolor: "#fff3e0", borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!loadedIrns) {
    return (
      <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading contract status...
        </Typography>
      </Box>
    );
  }

  // Compute categories
  const serbIrnKeys = Object.keys(serbIrns);
  const notLoadedKeys = Object.keys(notLoadedDistricts);
  const nonUnionKeys = Object.keys(nonUnionDistricts);

  const serbNotLoaded = serbIrnKeys.filter((irn) => !loadedIrns.has(irn));
  const waitingForSerb = notLoadedKeys.filter(
    (irn) => !serbIrns[irn] && !loadedIrns.has(irn)
  );

  const loadedCount = loadedIrns.size;
  const serbNotLoadedCount = serbNotLoaded.length;
  const waitingCount = waitingForSerb.length;
  const totalTracked = loadedCount + serbNotLoadedCount + waitingCount;
  const progressPct = totalTracked > 0 ? (loadedCount / totalTracked) * 100 : 0;

  const toggleCard = (card) => {
    setExpandedCard(expandedCard === card ? null : card);
  };

  const cards = [
    {
      key: "loaded",
      label: "In InsightBoard",
      count: loadedCount,
      color: "#2e7d32",
      bgColor: "#e8f5e9",
      borderColor: "#a5d6a7",
      icon: <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 28 }} />,
      districts: null, // Too many to list
      subtitle: "Districts with 2026 salary data loaded",
    },
    {
      key: "serb",
      label: "In SERB, Not Loaded",
      count: serbNotLoadedCount,
      color: "#e65100",
      bgColor: "#fff3e0",
      borderColor: "#ffcc80",
      icon: <WarningAmberIcon sx={{ color: "#e65100", fontSize: 28 }} />,
      districts: serbNotLoaded.map((irn) => serbIrns[irn]),
      subtitle: "New contracts available on SERB",
    },
    {
      key: "waiting",
      label: "Waiting for SERB",
      count: waitingCount,
      color: "#616161",
      bgColor: "#f5f5f5",
      borderColor: "#bdbdbd",
      icon: <HourglassEmptyIcon sx={{ color: "#616161", fontSize: 28 }} />,
      districts: waitingForSerb.map((irn) => notLoadedDistricts[irn]),
      subtitle: "No new contract on SERB yet",
    },
    {
      key: "nonunion",
      label: "No Union Contract",
      count: nonUnionKeys.length,
      color: "#5e35b1",
      bgColor: "#ede7f6",
      borderColor: "#b39ddb",
      icon: <DoNotDisturbOnOutlinedIcon sx={{ color: "#5e35b1", fontSize: 28 }} />,
      districts: nonUnionKeys.map((irn) => nonUnionDistricts[irn]),
      subtitle: "No teachers union agreement on SERB",
    },
  ];

  return (
    <Box sx={{ mt: 3, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
        2026 Contract Data Coverage
      </Typography>

      {/* Progress bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progressPct}
            sx={{
              height: 10,
              borderRadius: 5,
              bgcolor: "#e0e0e0",
              "& .MuiLinearProgress-bar": {
                borderRadius: 5,
                bgcolor: "#2e7d32",
              },
            }}
          />
        </Box>
        <Typography variant="body2" sx={{ minWidth: 48, fontWeight: "bold" }}>
          {Math.round(progressPct)}%
        </Typography>
      </Box>

      {/* Stat cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: 2,
          mb: 1,
        }}
      >
        {cards.map((card) => {
          const isExpandable = card.districts && card.districts.length > 0;
          const isExpanded = expandedCard === card.key;

          return (
            <Box key={card.key}>
              <Box
                onClick={() => isExpandable && toggleCard(card.key)}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: `1px solid ${card.borderColor}`,
                  bgcolor: card.bgColor,
                  cursor: isExpandable ? "pointer" : "default",
                  transition: "box-shadow 0.2s",
                  "&:hover": isExpandable
                    ? { boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }
                    : {},
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {card.icon}
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: card.color, lineHeight: 1 }}
                      >
                        {card.count}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {card.label}
                      </Typography>
                    </Box>
                  </Box>
                  {isExpandable && (
                    <IconButton size="small">
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", mt: 0.5 }}
                >
                  {card.subtitle}
                </Typography>
              </Box>

              {/* Expandable district list */}
              {isExpandable && (
                <Collapse in={isExpanded}>
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflowY: "auto",
                      border: `1px solid ${card.borderColor}`,
                      borderTop: "none",
                      borderRadius: "0 0 4px 4px",
                      bgcolor: "#fff",
                    }}
                  >
                    <List dense disablePadding>
                      {card.districts
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((d, i) => (
                          <ListItem
                            key={i}
                            sx={{
                              py: 0.25,
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            <ListItemText
                              primary={d.name}
                              secondary={d.county}
                              primaryTypographyProps={{ variant: "body2" }}
                              secondaryTypographyProps={{ variant: "caption" }}
                            />
                          </ListItem>
                        ))}
                    </List>
                  </Box>
                </Collapse>
              )}
            </Box>
          );
        })}
      </Box>

      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        SERB snapshot: {snapshotDate} | Loaded count updates live from database
      </Typography>
    </Box>
  );
}

export default SerbContractStatus;
